/**
 * `Intl.NumberFormat` wants an ISO code, but some call sites only hold a symbol
 * (the shop and store-credit screens read `currency_symbol` from the API). Codes
 * pass straight through; a symbol is mapped. "CHF" is already both.
 */
const CURRENCY_CODES: Record<string, string> = {
  '€': 'EUR',
  EUR: 'EUR',
  CHF: 'CHF',
  $: 'USD',
  '£': 'GBP',
};

const resolveCurrencyCode = (codeOrSymbol: string | null | undefined): string =>
  CURRENCY_CODES[(codeOrSymbol ?? '').trim()] ?? 'EUR';

/**
 * Formats a money amount for display, in the active locale.
 *
 * This is the single money shape in the app. It used to be four:
 * `formatMoney` gave "EUR 12,50", `formatMoneyWithSymbol` "12,50 €",
 * `formatStoreCredit` "12,50€ " with a trailing space, and
 * `UpdateSubscriptionPriceDetails` rendered "12,50EUR" inline — so two screens
 * side by side disagreed about how a price looks. They are now all this.
 *
 * `style: 'currency'` rather than a hand-assembled `"<code> <amount>"`, which is
 * what makes the symbol and its position follow the locale: German and French
 * put it after ("12,50 €"), English before ("€12.50"), and Swiss francs get the
 * "CHF" prefix in every language. Assembling it by hand is what produced four
 * shapes in the first place, because each call site made its own choice.
 *
 * Accepts a code or a symbol, since call sites have one or the other.
 *
 * Pass `intl.locale`.
 */
export const formatMoney = (
  value: number | string | null | undefined,
  currencyCodeOrSymbol: string,
  locale: string,
  fractionDigits = 2,
): string => {
  const numeric = typeof value === 'string' ? parseFloat(value) : value;
  const safe = Number.isFinite(numeric as number) ? (numeric as number) : 0;

  return safe.toLocaleString(locale, {
    style: 'currency',
    currency: resolveCurrencyCode(currencyCodeOrSymbol),
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

/**
 * Kept as a distinct name because the shop screens read a `currency_symbol` from
 * the API rather than a code, so their call sites read more clearly this way.
 * It is the same formatter — the app has one money shape.
 *
 * Pass `intl.locale`.
 */
export const formatMoneyWithSymbol = (
  value: number,
  currencySymbol: string,
  locale: string,
): string => formatMoney(value, currencySymbol, locale);

/**
 * A bare two-decimal amount with no currency code, in the active locale.
 *
 * For the call sites that render the code themselves — `{currencyCode} {amount}`.
 * Replaces `formatPrice` in pet-profile-update/utils, which was
 * `price.toFixed(2).replace('.', ',')`: correct German, since toFixed adds no
 * thousands separator, but German in every language.
 *
 * Pass `intl.locale`.
 */
export const formatAmount = (value: number, locale: string): string =>
  value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * The Pfotenpunkte balance. Same money shape as everything else; the only
 * difference is that a whole balance drops the decimals — "5 €", not "5,00 €" —
 * which is what the previous inline version did via `Number.isInteger`.
 *
 * This existed as the same six lines copied into StoreCreditPage,
 * StoreCreditPfotenpunkteCard and ShopPage, each ending in
 * `.toFixed(2).replace('.', ',')` — a hardcoded German decimal comma in all three,
 * and each emitting a stray trailing space after the symbol.
 *
 * Takes both a code and a symbol because the three call sites have both to hand
 * and either can be the one that says CHF.
 *
 * Pass `intl.locale`.
 */
export const formatStoreCredit = (
  balance: number,
  currencyCode: string,
  currencySymbol: string,
  locale: string,
): string =>
  formatMoney(
    balance,
    currencyCode === 'CHF' || currencySymbol === 'CHF' ? 'CHF' : currencyCode || currencySymbol,
    locale,
    Number.isInteger(balance) ? 0 : 2,
  );

/*
 * There is deliberately no date helper here. The four hardcoded
 * `toLocaleDateString('de-AT', …)` calls became `<FormattedDate>` where the result
 * is rendered directly, and `intl.formatDate` where a string is needed inside a
 * `useMemo` — both already follow the active locale, so a wrapper would only add
 * a second way to do the same thing.
 */
